'use client';

import * as React from 'react';
import { CheckCircle2, XCircle, AlertTriangle, RotateCcw, Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';

import type { OperationStatus } from '../types';

interface ResultsPanelProps {
  operationStatus: OperationStatus;
  onRollback?: () => void;
  isRollingBack?: boolean;
}

const STATUS_CONFIG = {
  completed: {
    icon: CheckCircle2,
    color: 'text-green-700',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    label: 'Completed Successfully',
  },
  partially_completed: {
    icon: AlertTriangle,
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    label: 'Partially Completed',
  },
  failed: {
    icon: XCircle,
    color: 'text-red-700',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    label: 'Failed',
  },
  rolled_back: {
    icon: RotateCcw,
    color: 'text-gray-700',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    label: 'Rolled Back',
  },
};

export function ResultsPanel({
  operationStatus,
  onRollback,
  isRollingBack,
}: ResultsPanelProps) {
  const config = STATUS_CONFIG[operationStatus.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.failed;
  const Icon = config.icon;

  const failedResults = operationStatus.results.filter((r) => r.status === 'failed');
  const skippedResults = operationStatus.results.filter((r) => r.status === 'skipped');

  return (
    <div className="space-y-6 p-1">
      {/* Status banner */}
      <Alert className={`${config.bgColor} ${config.borderColor}`}>
        <Icon className={`h-5 w-5 ${config.color}`} />
        <AlertTitle className={config.color}>{config.label}</AlertTitle>
        <AlertDescription className={config.color}>
          Operation completed at {operationStatus.completedAt ? new Date(operationStatus.completedAt).toLocaleString() : 'N/A'}
        </AlertDescription>
      </Alert>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="text-2xl font-bold">
              {operationStatus.targetCount.toLocaleString()}
            </div>
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-green-50/50">
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-medium text-green-700 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Success
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="text-2xl font-bold text-green-700">
              {operationStatus.successCount.toLocaleString()}
            </div>
          </CardContent>
        </Card>

        <Card className={operationStatus.failedCount > 0 ? 'border-red-200 bg-red-50/50' : ''}>
          <CardHeader className="py-3">
            <CardTitle className={`text-sm font-medium flex items-center gap-2 ${operationStatus.failedCount > 0 ? 'text-red-700' : 'text-muted-foreground'}`}>
              <XCircle className="h-4 w-4" />
              Failed
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className={`text-2xl font-bold ${operationStatus.failedCount > 0 ? 'text-red-700' : ''}`}>
              {operationStatus.failedCount.toLocaleString()}
            </div>
          </CardContent>
        </Card>

        <Card className={operationStatus.skippedCount > 0 ? 'border-yellow-200 bg-yellow-50/50' : ''}>
          <CardHeader className="py-3">
            <CardTitle className={`text-sm font-medium flex items-center gap-2 ${operationStatus.skippedCount > 0 ? 'text-yellow-700' : 'text-muted-foreground'}`}>
              <AlertTriangle className="h-4 w-4" />
              Skipped
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className={`text-2xl font-bold ${operationStatus.skippedCount > 0 ? 'text-yellow-700' : ''}`}>
              {operationStatus.skippedCount.toLocaleString()}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Failed subscriptions list */}
      {failedResults.length > 0 && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-medium text-red-700">
              Failed Subscriptions ({failedResults.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ScrollArea className="h-40 rounded-md border p-2">
              <div className="space-y-2">
                {failedResults.slice(0, 50).map((result) => (
                  <div
                    key={result.subscriptionId}
                    className="flex items-center justify-between text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <XCircle className="h-3 w-3 text-red-500" />
                      <span className="font-mono text-xs">{result.subscriptionId}</span>
                    </div>
                    <span className="text-xs text-muted-foreground truncate max-w-[300px]">
                      {result.error || 'Unknown error'}
                    </span>
                  </div>
                ))}
                {failedResults.length > 50 && (
                  <div className="text-center text-xs text-muted-foreground py-2">
                    ... and {failedResults.length - 50} more
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Rollback option */}
      {operationStatus.canRollback && onRollback && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium">Rollback Available</h4>
                <p className="text-sm text-muted-foreground">
                  You can undo this operation and restore previous values.
                </p>
              </div>
              <Button
                variant="outline"
                onClick={onRollback}
                disabled={isRollingBack}
              >
                {isRollingBack ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Rolling back...
                  </>
                ) : (
                  <>
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Rollback
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
